/**
 * CreateRoleModal Component
 *
 * Modal for creating new global roles with name, description, and permissions.
 * Uses ResourceScopedPermissionBuilder for user-friendly permission editing.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Three role types:
 *   - tenant: Controls tenant management features (iam, elt, app, etc.)
 *   - application: Controls Ketrics resource permissions for applications
 *   - app_domain: Defines app-level permissions passed to tenant applications
 * - Permissions format: ResourceScopedPermission[] with { actions: string[], resources: string[] }
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { Select } from '../../../components/ui/Select';
import { ResourceScopedPermissionBuilder } from './ResourceScopedPermissionBuilder';
import { useCreateRole } from '../hooks/useCreateRole';
import { useAuth } from '../../../contexts/AuthContext';
import type { ResourceScopedPermission, RoleType } from '../../../types/role.types';
import { RoleType as RoleTypeEnum } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Role type options for the selector
 */
const ROLE_TYPE_OPTIONS = [
  {
    value: RoleTypeEnum.APPLICATION,
    label: 'Application Role',
    description: 'Controls Ketrics resource permissions for applications (volumes, secrets, etc.)',
  },
  {
    value: RoleTypeEnum.TENANT,
    label: 'Tenant Role',
    description: 'Controls tenant management features (users, groups, applications, etc.)',
  },
  {
    value: RoleTypeEnum.APP_DOMAIN,
    label: 'App Domain Role',
    description: 'Defines app-level permissions passed to tenant applications (enforcement is app-side)',
  },
];

/**
 * Generate role code from name
 * Converts "Inventory Manager" to "inventory-manager"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

const createRoleSchema = z.object({
  roleCode: z
    .string()
    .min(3, 'Code must be at least 3 characters')
    .max(50, 'Code must not exceed 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Code must contain only lowercase letters, numbers, and hyphens'),
  roleName: z.string().min(1, 'Role name is required').max(100),
  description: z.string().max(500).optional(),
});

type CreateRoleFormData = z.infer<typeof createRoleSchema>;

interface CreateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';

  // Use ResourceScopedPermission[] directly - no conversion needed
  const [permissions, setPermissions] = useState<ResourceScopedPermission[]>([]);
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [roleType, setRoleType] = useState<RoleType>(RoleTypeEnum.APPLICATION);
  const [path, setPath] = useState<string>('/');

  // Reset permissions when role type changes (different permission domains)
  const handleRoleTypeChange = (newType: RoleType) => {
    setRoleType(newType);
    setPermissions([]);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateRoleFormData>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      roleCode: '',
      roleName: '',
      description: '',
    },
  });

  const watchName = watch('roleName');

  // Auto-generate role code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('roleCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const createRoleMutation = useCreateRole(tenantId);

  const onSubmit = async (data: CreateRoleFormData) => {
    // Validate permissions - must have at least one permission group with actions
    const hasValidPermissions = permissions.some((p) => p.actions.length > 0);
    if (!hasValidPermissions) {
      alert('Please add at least one permission with actions');
      return;
    }

    // Filter out empty permission groups
    const validPermissions = permissions.filter((p) => p.actions.length > 0);

    try {
      await createRoleMutation.mutateAsync({
        roleCode: data.roleCode,
        roleName: data.roleName,
        description: data.description,
        path: path || '/',
        type: roleType,
        permissions: validPermissions,
      });

      // Reset form
      reset();
      setPermissions([]);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to create role:', error);
    }
  };

  const handleClose = () => {
    reset();
    setPermissions([]);
    setAutoGenerateCode(true);
    setRoleType(RoleTypeEnum.APPLICATION);
    setPath('/');
    createRoleMutation.reset();
    onClose();
  };

  // Check if form can be submitted
  const hasValidPermissions = permissions.some((p) => p.actions.length > 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create New Role</Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            {/* Error Alert */}
            {createRoleMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(createRoleMutation.error)}
              </Alert>
            )}

            {/* Role Name */}
            <div>
              <label htmlFor="roleName" className="block text-sm font-medium text-gray-700">
                Role Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="roleName"
                type="text"
                placeholder="e.g., Inventory Manager"
                {...register('roleName')}
                error={errors.roleName?.message}
                className="mt-1"
              />
            </div>

            {/* Role Code */}
            <div>
              <label htmlFor="roleCode" className="block text-sm font-medium text-gray-700">
                Role Code <span className="text-red-500">*</span>
              </label>
              <Input
                id="roleCode"
                type="text"
                placeholder="e.g., inventory-manager"
                {...register('roleCode')}
                error={errors.roleCode?.message}
                className="mt-1"
                disabled={autoGenerateCode}
              />
              <div className="mt-2 flex items-center">
                <Checkbox
                  id="autoGenerateRoleCode"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                />
                <label htmlFor="autoGenerateRoleCode" className="ml-2 text-sm text-gray-600">
                  Auto-generate code from name
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <Textarea
                id="description"
                rows={3}
                placeholder="Describe the role's purpose and responsibilities"
                {...register('description')}
                error={errors.description?.message}
                className="mt-1"
              />
            </div>

            {/* Path */}
            <div>
              <label htmlFor="path" className="block text-sm font-medium text-gray-700">
                Path
              </label>
              <Input
                id="path"
                type="text"
                placeholder="e.g., /finance/reports"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-gray-500">
                Organizational path for grouping roles. Use "/" for root or a hierarchy like "/finance/reports".
              </p>
            </div>

            {/* Role Type */}
            <div>
              <label htmlFor="roleType" className="block text-sm font-medium text-gray-700">
                Role Type <span className="text-red-500">*</span>
              </label>
              <Select
                id="roleType"
                value={roleType}
                onChange={(e) => handleRoleTypeChange(e.target.value as RoleType)}
                className="mt-1"
              >
                {ROLE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                {ROLE_TYPE_OPTIONS.find((o) => o.value === roleType)?.description}
              </p>
            </div>

            {/* Permissions Builder */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions <span className="text-red-500">*</span>
              </label>
              <ResourceScopedPermissionBuilder
                tenantId={tenantId}
                permissions={permissions}
                onChange={setPermissions}
                roleType={roleType}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createRoleMutation.isPending}
            disabled={!hasValidPermissions}
          >
            Create Role
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
